import { isContentAdminRoute } from '~/composables/useContentLockedRoute';
import {
  UNIFIED_ROUTE_ACCEPT_TERMS,
  UNIFIED_ROUTE_AUTH,
  UNIFIED_ROUTE_LOGIN,
  UNIFIED_ROUTE_ONBOARDING,
  UNIFIED_ROUTE_PRIVACY_POLICY,
  UNIFIED_ROUTE_SIGN_IN_QUESTIONS,
  UNIFIED_ROUTE_START,
  UNIFIED_ROUTE_TERMS,
} from '~/constants/routes/unified';
import { useConfigStore } from '~/stores/config';
import { AdminMfaRouteDecision, adminMfaRouteDecision } from '~/utils/adminMfaRoute';

// The sign-in surfaces stay reachable; the terms interstitial runs first (03.cluster-terms-acceptance
// sorts before this file) and must not be interrupted; the legal pages are readable from the flow;
// /start resolves a shared lesson code before anything else. The provider login and callback pages
// and the failure page work while the last person is still signed in on a shared device, as the
// terms gate allows; the gate catches the new person on the landing the callback navigates to. The
// school invite doors offer a sign-out when someone else is signed in, and joining a school clears
// the question, so they are left alone and the gate catches up on the next page. The admin app is
// never interrupted.
const EXEMPT_PATHS = [
  UNIFIED_ROUTE_SIGN_IN_QUESTIONS,
  UNIFIED_ROUTE_ACCEPT_TERMS,
  UNIFIED_ROUTE_AUTH,
  UNIFIED_ROUTE_LOGIN,
  UNIFIED_ROUTE_ONBOARDING,
  UNIFIED_ROUTE_START,
  '/accept-invite',
  '/join',
  UNIFIED_ROUTE_TERMS,
  UNIFIED_ROUTE_PRIVACY_POLICY,
];

/**
 * Sign-in questions gate (UX half; what is asked is decided server-side in
 * SignInQuestionService and surfaced as `user.signInQuestions`). A consumer
 * student with something to answer is sent to the Blinky flow, carrying the
 * intended path to return to. Not asking is the safe side, so there is no
 * backend refusal to pair with this.
 *
 * Like the terms gate, the decision is made once, at login, and kept on the
 * session: a flag turning on, a role change or a consent row we wrote without
 * asking never pulls a running session into the flow; the student is asked at
 * their next login. This middleware only routes on the stored decision. Do not
 * re-decide it per request here or in the API.
 *
 * An admin who still owes the second factor is left to the admin MFA gate (03a),
 * which would otherwise send them back to the admin login and loop.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  if (useConfigStore().isSchoolsCluster) {
    return;
  }

  const { user } = useAuth();
  if (!user.value?.signInQuestions || adminMfaRouteDecision({ path: to.path, status: user.value.adminMfaStatus }) !== AdminMfaRouteDecision.none) {
    return;
  }

  const path = clusterRelativePath(to.path);
  if (isContentAdminRoute(path) || EXEMPT_PATHS.some(exempt => path === exempt || path.startsWith(`${exempt}/`))) {
    return;
  }

  return await navigateTo(
    { path: clusterRedirectPath(UNIFIED_ROUTE_SIGN_IN_QUESTIONS), query: { redirect: clusterRelativePath(to.fullPath) } },
    { replace: true },
  );
});
